import { Flex, Text, IconButton, Separator, Button } from "@radix-ui/themes";
import { PlusIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { EscalationRuleOperationType } from "@/v2/entities/escalation-rule/EscalationRule.model";
import { EscalationRuleFormData } from "../../EscalationRuleForm";
import { useCallback, useMemo, useState } from "react";
import { DropdownMenu } from "@/components/shared/dropdown-menu";
import { Enrichment } from "@/v2/entities/enrichment/Enrichment.model";

interface TagsSectionProps {
  value: EscalationRuleFormData;
  onChange: (updates: Partial<EscalationRuleFormData>) => void;
  onRemove: () => void;
  tags: string[];
  setTags: (tags: string[]) => void;
  enrichments: Enrichment[];
  loadingEnrichments: boolean;
}

export function TagsSection({
  value,
  onChange,
  onRemove,
  tags,
  setTags,
  enrichments,
  loadingEnrichments,
}: TagsSectionProps) {
  const [newTag, setNewTag] = useState<string>("");

  const addTag = useCallback(() => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      const newTagValue = newTag.trim();
      setTags([...tags, newTagValue]);
      setNewTag("");

      const newOperations = [
        ...value.escalationRuleOperations,
        {
          type: EscalationRuleOperationType.ADD_TAG,
          enabled: true,
          key: null,
          value: newTagValue,
        },
      ];
      onChange({ escalationRuleOperations: newOperations });
    }
  }, [newTag, tags, value.escalationRuleOperations, onChange, setTags]);

  const removeTag = useCallback(
    (tagToRemove: string) => {
      setTags(tags.filter((tag) => tag !== tagToRemove));

      const filteredOperations = value.escalationRuleOperations.filter(
        (operation) =>
          !(
            operation.type === EscalationRuleOperationType.ADD_TAG &&
            operation.value === tagToRemove
          )
      );
      onChange({ escalationRuleOperations: filteredOperations });
    },
    [tags, value.escalationRuleOperations, onChange, setTags]
  );

  const addQuackTopicTag = useCallback(() => {
    const quackTopicTag = "{Topic}";
    if (!tags.includes(quackTopicTag)) {
      setTags([...tags, quackTopicTag]);
      const newOperations = [
        ...value.escalationRuleOperations,
        {
          type: EscalationRuleOperationType.ADD_TAG,
          enabled: true,
          key: null,
          value: quackTopicTag,
        },
      ];
      onChange({ escalationRuleOperations: newOperations });
    }
  }, [tags, value.escalationRuleOperations, onChange, setTags]);

  const addEnrichmentTag = useCallback(
    (enrichmentName: string) => {
      const enrichmentTag = `{${enrichmentName}}`;
      if (!tags.includes(enrichmentTag)) {
        setTags([...tags, enrichmentTag]);
        const newOperations = [
          ...value.escalationRuleOperations,
          {
            type: EscalationRuleOperationType.ADD_TAG,
            enabled: true,
            key: null,
            value: enrichmentTag,
          },
        ];
        onChange({ escalationRuleOperations: newOperations });
      }
    },
    [tags, value.escalationRuleOperations, onChange, setTags]
  );

  const addIsAuthenticatedTag = useCallback(() => {
    const isAuthenticatedTag = "{is_authenticated}";
    if (!tags.includes(isAuthenticatedTag)) {
      setTags([...tags, isAuthenticatedTag]);
      const newOperations = [
        ...value.escalationRuleOperations,
        {
          type: EscalationRuleOperationType.ADD_TAG,
          enabled: true,
          key: null,
          value: isAuthenticatedTag,
        },
      ];
      onChange({ escalationRuleOperations: newOperations });
    }
  }, [tags, value.escalationRuleOperations, onChange, setTags]);

  const dropdownItems = useMemo(() => {
    const topicOption = {
      label: "✨ Topic",
      onClick: addQuackTopicTag,
    };

    const isAuthenticatedOption = {
      label: "🔐 Is Authenticated",
      onClick: addIsAuthenticatedTag,
    };

    const enrichmentOptions = enrichments.map((enrichment) => ({
      label: enrichment.name,
      onClick: () => addEnrichmentTag(enrichment.name),
    }));

    if (enrichmentOptions.length === 0) {
      return [topicOption, isAuthenticatedOption];
    }

    return [
      topicOption,
      isAuthenticatedOption,
      {
        label: "Enrichments",
        items: enrichmentOptions,
      },
    ];
  }, [addQuackTopicTag, addIsAuthenticatedTag, addEnrichmentTag, enrichments]);

  return (
    <>
      <Separator
        orientation="horizontal"
        size="4"
        style={{ marginTop: "12px" }}
      />
      <Flex direction={"column"} gap={"2"} mt="4">
        <Flex justify="between" align="center">
          <Text weight="bold" size="2">
            Tags
          </Text>
          <IconButton color="gray" variant="ghost" size="1" onClick={onRemove}>
            <PlusIcon
              width={16}
              height={16}
              style={{ transform: "rotate(45deg)" }}
            />
          </IconButton>
        </Flex>
        <Text size="2" color="gray">
          Add tags to help categorize escalated tickets
        </Text>
        {/* Add Tag Input */}
        <Flex gap="2" align="center">
          <Input
            placeholder="Enter tag name..."
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                addTag();
              }
            }}
            style={{ flex: 1 }}
          />

          <IconButton
            color="gray"
            variant="ghost"
            size="1"
            onClick={addTag}
            disabled={!newTag.trim() || tags.includes(newTag.trim())}
          >
            <PlusIcon width={16} height={16} />
          </IconButton>
        </Flex>
        <Flex align="center" gap="2" mt="2">
          <Separator orientation="horizontal" size="4" /> <Text>or</Text>{" "}
          <Separator orientation="horizontal" size="4" />
        </Flex>
        <Flex align="center" gap="2">
          {dropdownItems.length > 0 && (
            <DropdownMenu
              items={dropdownItems.map((option) => {
                if ("items" in option) {
                  return {
                    label: option.label,
                    items: option.items.map((item) => ({
                      ...item,
                      icon: <PlusIcon size={14} />,
                    })),
                  };
                }
                return {
                  label: option.label,
                  onClick: option.onClick,
                  icon: <PlusIcon size={14} />,
                };
              })}
            >
              <DropdownMenu.Trigger>
                <Button
                  color="gray"
                  variant="surface"
                  highContrast
                  size="2"
                  mb="3"
                >
                  <PlusIcon size={15} />
                  Add Dynamic Tag
                </Button>
              </DropdownMenu.Trigger>
            </DropdownMenu>
          )}
        </Flex>
        {/* Display Tags */}
        {tags.length > 0 && (
          <Flex gap="1" wrap="wrap" mt="2">
            {tags.map((tag, index) => (
              <Flex
                key={index}
                align="center"
                gap="1"
                style={{
                  backgroundColor: "var(--gray-3)",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  border: "1px solid var(--gray-6)",
                }}
              >
                <Text size="1">{tag}</Text>
                <IconButton
                  color="gray"
                  variant="ghost"
                  size="1"
                  onClick={() => removeTag(tag)}
                  style={{ padding: "2px" }}
                >
                  <PlusIcon
                    width={12}
                    height={12}
                    style={{ transform: "rotate(45deg)" }}
                  />
                </IconButton>
              </Flex>
            ))}
          </Flex>
        )}
      </Flex>
    </>
  );
}
